import { useId, useMemo, useState } from "react";

import { LAB_BADGE_KINDS, LAB_BADGE_LABELS } from "@/lib/labModel";
import {
  buildRunComparison,
  differingTurnCount,
  type LabCompareTurn,
  type LabRunModel,
} from "@/lib/labRunModel";
import {
  OPERATOR_LABELS,
  describeOperator,
  formatScalar,
} from "@/lib/previewDecisions";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/ui/Card";
import { EmptyState } from "@/ui/states";

/**
 * Compare: run A (the primary run) beside run B, beyond the five signatures.
 * Everything shown is text-free: the producers' scalars, thresholds,
 * dispositions and counts. Nothing is computed here; `buildRunComparison`
 * does the alignment and flags each difference.
 */

type Props = {
  projectId: string;
  primary: LabRunModel;
  compare: LabRunModel | null;
  selectedTurn: number | null;
  onSelectTurn: (turn: number) => void;
};

const NONE = "none recorded";

function operatorText(operator: string): string {
  return describeOperator(
    operator in OPERATOR_LABELS
      ? (operator as keyof typeof OPERATOR_LABELS)
      : { unknown: operator },
  );
}

type Criterion = LabCompareTurn["criteria"][number];
type CriterionSide = NonNullable<Criterion["a"]>;
type Alternative = LabCompareTurn["whyNot"][number];
type AlternativeSide = NonNullable<Alternative["a"]>;

function DiffersBadge() {
  return <Badge tone="warning">differs</Badge>;
}

function resultText(side: CriterionSide | null): string {
  return side === null ? "not in this run" : side.passed ? "passed" : "failed";
}

function scoreText(score: AlternativeSide["comparedScore"]): string {
  return score === null || score === undefined
    ? NONE
    : `${score.value} ${score.scale}`;
}

function TableBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-max text-sm">{children}</table>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th scope="col" className="py-1 pe-3 text-start font-medium">
      {children}
    </th>
  );
}

function TurnRow({
  row,
  onSelectTurn,
}: {
  row: LabCompareTurn;
  onSelectTurn: (turn: number) => void;
}) {
  const differs = differingTurnCount([row]) > 0;
  const kinds = row.signatureDiff
    ? LAB_BADGE_KINDS.filter((kind) => row.signatureDiff?.[kind])
    : [];
  return (
    <li>
      <details className="rounded-md border border-border bg-card">
        <summary className="flex cursor-pointer flex-wrap items-center gap-2 px-3 py-2 text-sm focus-visible:outline-2 focus-visible:outline-ring">
          <span className="font-semibold text-foreground">
            Turn {row.turnNumber}
          </span>
          {row.onlyIn !== null ? (
            <Badge tone="accent">
              Only in run {row.onlyIn === "a" ? "A" : "B"}
            </Badge>
          ) : differs ? (
            <DiffersBadge />
          ) : (
            <span className="text-muted-foreground">same</span>
          )}
          {kinds.map((kind) => (
            <Badge key={kind} tone="outline">
              {LAB_BADGE_LABELS[kind]}
            </Badge>
          ))}
        </summary>
        <div className="space-y-4 border-t border-border p-3">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span>
              <strong>A:</strong> {row.positions.a ?? NONE}
            </span>
            <span>
              <strong>B:</strong> {row.positions.b ?? NONE}
            </span>
            {row.positions.differs ? <DiffersBadge /> : null}
          </div>

          <div>
            <h4 className="text-xs font-medium text-muted-foreground">
              Criteria
            </h4>
            {row.criteria.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No criterion recorded.
              </p>
            ) : (
              <TableBox>
                <thead>
                  <tr className="border-b border-border text-xs text-muted-foreground">
                    <Th>Criterion</Th>
                    <Th>Observed A</Th>
                    <Th>Observed B</Th>
                    <Th>Threshold</Th>
                    <Th>Result A</Th>
                    <Th>Result B</Th>
                    <Th>Differs</Th>
                  </tr>
                </thead>
                <tbody>
                  {row.criteria.map((c) => {
                    const ref = c.a ?? c.b;
                    return (
                      <tr
                        key={c.criterionId}
                        className="border-b border-border last:border-0"
                      >
                        <td className="py-1 pe-3">{c.criterionId}</td>
                        <td className="py-1 pe-3">
                          {c.a ? formatScalar(c.a.observed) : "not in this run"}
                        </td>
                        <td className="py-1 pe-3">
                          {c.b ? formatScalar(c.b.observed) : "not in this run"}
                        </td>
                        <td className="py-1 pe-3">
                          {ref
                            ? `${operatorText(ref.operator)} ${formatScalar(ref.threshold)}`
                            : NONE}
                        </td>
                        <td className="py-1 pe-3">{resultText(c.a)}</td>
                        <td className="py-1 pe-3">{resultText(c.b)}</td>
                        <td className="py-1">
                          {c.differs ? <DiffersBadge /> : "same"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </TableBox>
            )}
          </div>

          <div>
            <h4 className="text-xs font-medium text-muted-foreground">
              Why-not alternatives
            </h4>
            {row.whyNot.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No alternative recorded.
              </p>
            ) : (
              <TableBox>
                <thead>
                  <tr className="border-b border-border text-xs text-muted-foreground">
                    <Th>Alternative</Th>
                    <Th>Disposition A</Th>
                    <Th>Disposition B</Th>
                    <Th>Reason A</Th>
                    <Th>Reason B</Th>
                    <Th>Compared score A</Th>
                    <Th>Compared score B</Th>
                    <Th>Differs</Th>
                  </tr>
                </thead>
                <tbody>
                  {row.whyNot.map((w) => (
                    <tr
                      key={w.alternative}
                      className="border-b border-border last:border-0"
                    >
                      <td className="py-1 pe-3">{w.alternative}</td>
                      <td className="py-1 pe-3">
                        {w.a ? w.a.disposition : "not in this run"}
                      </td>
                      <td className="py-1 pe-3">
                        {w.b ? w.b.disposition : "not in this run"}
                      </td>
                      <td className="py-1 pe-3">
                        {w.a ? (w.a.reasonCode ?? NONE) : "not in this run"}
                      </td>
                      <td className="py-1 pe-3">
                        {w.b ? (w.b.reasonCode ?? NONE) : "not in this run"}
                      </td>
                      <td className="py-1 pe-3">
                        {w.a ? scoreText(w.a.comparedScore) : "not in this run"}
                      </td>
                      <td className="py-1 pe-3">
                        {w.b ? scoreText(w.b.comparedScore) : "not in this run"}
                      </td>
                      <td className="py-1">
                        {w.differs ? <DiffersBadge /> : "same"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TableBox>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span>
              <strong>Prompt calls A:</strong> {row.promptCalls.a.count}
              {" · "}
              {row.promptCalls.a.totalMs === null
                ? "latency not recorded"
                : `${row.promptCalls.a.totalMs} ms`}
            </span>
            <span>
              <strong>B:</strong> {row.promptCalls.b.count}
              {" · "}
              {row.promptCalls.b.totalMs === null
                ? "latency not recorded"
                : `${row.promptCalls.b.totalMs} ms`}
            </span>
            {row.promptCalls.differs ? <DiffersBadge /> : null}
          </div>

          <Button
            size="sm"
            variant="outline"
            onClick={() => onSelectTurn(row.turnNumber)}
          >
            Show turn
            <span className="sr-only"> {row.turnNumber}</span>
          </Button>
        </div>
      </details>
    </li>
  );
}

export function LabCompareTab({ primary, compare, onSelectTurn }: Props) {
  const headingId = useId();
  const [onlyDiffering, setOnlyDiffering] = useState(true);
  const comparison = useMemo(
    () => (compare === null ? null : buildRunComparison(primary, compare)),
    [primary, compare],
  );

  if (comparison === null) {
    return (
      <section aria-labelledby={headingId}>
        <h2
          id={headingId}
          className="mb-3 text-base font-semibold text-foreground"
        >
          Compare
        </h2>
        <EmptyState
          title="No second run to compare"
          description="Arm Compare in the top bar and pick a second run to see both side by side."
        />
      </section>
    );
  }

  const differing = differingTurnCount(comparison.rows);
  const shown = onlyDiffering
    ? comparison.rows.filter((row) => differingTurnCount([row]) > 0)
    : comparison.rows;
  const cell = (value: string | number | boolean | null | undefined) =>
    value === null || value === undefined ? NONE : String(value);

  return (
    <section aria-labelledby={headingId} className="space-y-4">
      <h2 id={headingId} className="text-base font-semibold text-foreground">
        Compare
      </h2>

      <Card>
        <CardHeader>
          <CardTitle>Final state</CardTitle>
        </CardHeader>
        <CardBody>
          <TableBox>
            <thead>
              <tr className="border-b border-border text-xs text-muted-foreground">
                <Th>Measure</Th>
                <Th>Run A</Th>
                <Th>Run B</Th>
                <Th>Differs</Th>
              </tr>
            </thead>
            <tbody>
              {comparison.summary.map((row) => (
                <tr
                  key={row.label}
                  className="border-b border-border last:border-0"
                >
                  <th scope="row" className="py-1 pe-3 text-start font-medium">
                    {row.label}
                  </th>
                  <td className="py-1 pe-3">{cell(row.a)}</td>
                  <td className="py-1 pe-3">{cell(row.b)}</td>
                  <td className="py-1">
                    {row.differs ? <DiffersBadge /> : "same"}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableBox>
        </CardBody>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p role="status" className="text-sm text-foreground">
          {differing} of {comparison.rows.length} turns differ
        </p>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={onlyDiffering}
            onChange={(event) => setOnlyDiffering(event.currentTarget.checked)}
          />
          Show only differing turns
        </label>
      </div>

      {shown.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {comparison.rows.length === 0
            ? "Neither run has recorded turns."
            : "No turn differs."}
        </p>
      ) : (
        <ul className="space-y-2">
          {shown.map((row) => (
            <TurnRow
              key={row.turnNumber}
              row={row}
              onSelectTurn={onSelectTurn}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
